import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, type EngineInfo } from "../lib/api";
import { useConversation } from "../lib/useConversation";
import { Thread } from "../components/Thread";
import { Composer } from "../components/Composer";
import { EnginePicker } from "../components/EnginePicker";
import { HistoryDrawer, HistoryIcon } from "../components/HistoryDrawer";
import { setLastChatId } from "../lib/lastChat";
import { TopbarSettings } from "../components/TopbarSettings";

export function ChatView({ intro }: { intro: string }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Deep link from anywhere in the app: /chat?ask=<question>.
  const ask = params.get("ask") ?? "";
  const convId = id ?? null;

  const chat = useConversation(convId);
  const missing = Boolean(convId) && chat.error === "conversation not found";
  const [engines, setEngines] = useState<EngineInfo[]>([]);
  const [engine, setEngine] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [buildMode, setBuildMode] = useState(false);
  const historyBtn = useRef<HTMLButtonElement>(null);

  const closeHistory = useCallback(() => {
    setHistoryOpen(false);
    historyBtn.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .engines()
      .then(({ engines: list }) => {
        if (cancelled) return;
        setEngines(list);
        setEngine((cur) => cur || list.find((e) => e.available)?.id || list[0]?.id || "");
      })
      .catch(() => {
        /* the picker degrades to "unavailable"; chat still renders */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A loaded conversation dictates its own engine.
  useEffect(() => {
    if (chat.conversation?.engine) setEngine(chat.conversation.engine);
    if (chat.conversation?.model) setModel(chat.conversation.model);
    if (chat.conversation) setBuildMode(Boolean(chat.conversation.buildMode));
  }, [chat.conversation?.engine, chat.conversation?.model, chat.conversation?.buildMode, chat.conversation]);

  useEffect(() => {
    if (convId && chat.conversation) setLastChatId(convId);
  }, [convId, chat.conversation]);

  useEffect(() => {
    if (!convId) setBuildMode(false);
  }, [convId]);

  useEffect(() => {
    if (!missing) return;
    setLastChatId(null);
    navigate("/chat", { replace: true });
  }, [missing, navigate]);

  const active = engines.find((e) => e.id === engine);
  const blocked = engines.length > 0 && !active?.available;

  const send = async (text: string, attachments: Parameters<typeof chat.send>[0]["attachments"]) => {
    const newId = await chat.send({ message: text, attachments, engine, model, buildMode });
    // First message in a fresh chat: give it a URL so back/forward and reload work.
    if (!convId && newId) navigate(`/chat/${newId}`, { replace: true });
  };

  return (
    <div className="page chat-page">
      <header className="topbar">
        <h1 className="topbar__title truncate">
          {chat.conversation?.title || "New chat"}
        </h1>
        {buildMode && (
          <span className="pill pill--warn" title="This chat can edit files and run commands">
            Build
          </span>
        )}
        <div className="topbar__right">
          {(!chat.connected || chat.reconnecting) && (
            <span className="pill pill--warn">Reconnecting…</span>
          )}
          <button
            ref={historyBtn}
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={() => setHistoryOpen(true)}
            aria-label="Conversation history"
            aria-expanded={historyOpen}
            aria-haspopup="dialog"
            title="History"
          >
            <HistoryIcon />
          </button>
          <EnginePicker
            engines={engines}
            engineId={engine}
            model={model}
            engineLocked={chat.messages.length > 0 && Boolean(chat.conversation)}
            modelLocked={chat.busy}
            buildMode={buildMode}
            onChange={(e, m) => {
              setEngine(e);
              setModel(m);
            }}
            onBuildMode={(on) => {
              setBuildMode(on);
              if (convId) void api.setBuildMode(convId, on);
            }}
          />
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={() => navigate("/chat")}
            aria-label="New chat"
            title="New chat"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          <TopbarSettings />
        </div>
      </header>

      {blocked && active && (
        <div className="banner banner--warn">
          <strong>{active.label} is unavailable.</strong>
          <span>{active.detail}</span>
        </div>
      )}
      {chat.error && !missing && <div className="banner banner--fault">{chat.error}</div>}

      <Thread
        messages={chat.messages}
        busy={chat.busy}
        intro={intro}
        onRetry={(seq, text) => void chat.retryFrom(seq, text)}
        reconnecting={chat.reconnecting}
      />

      <Composer
        busy={chat.busy}
        disabled={blocked}
        initialText={ask}
        placeholder={blocked ? `${active?.label ?? "Engine"} unavailable` : "Message…"}
        onSend={send}
        onStop={() => void chat.stop()}
      />

      <HistoryDrawer
        open={historyOpen}
        activeId={convId ?? undefined}
        onClose={closeHistory}
        onPick={(id) => navigate(`/chat/${id}`)}
      />
    </div>
  );
}
